import React from "react";
import { FaExchangeAlt, FaLock, FaExternalLinkAlt } from "react-icons/fa";
import { useAccount, useConnect, useDisconnect } from "wagmi";
import { useDirectPay } from "../../hooks/useDirectPay";
import { ReceiptCard } from "./ReceiptCard";
import { getTxExplorerUrl } from "../../helper/x402/chains";

/**
 * Direct on-chain transfer payment panel (P3.2 v2).
 *
 * Three screens inside one panel (mirrors WalletPanel):
 *   1. Not connected → grid of wagmi connector buttons.
 *   2. Connected + flow states (idle / switching / sending /
 *      confirming / recording / error) → pay button + status strip.
 *   3. Success → ReceiptCard.
 *
 * Visible when the /info response carries `'direct'` in
 * `paymentMethods` — that's native-token links + any ERC-20 that
 * isn't in the EIP-3009 allowlist. The tab is hidden on
 * x402-only links (USDC + friends).
 */
export function DirectPanel({ link }) {
  const { address, isConnected } = useAccount();
  const { connect, connectors, isLoading: isConnecting, pendingConnector } =
    useConnect();
  const { disconnect } = useDisconnect();
  const {
    phase,
    txHash,
    confirmations,
    requiredConfirmations,
    receipt,
    error,
    pay,
    reset,
  } = useDirectPay(link);

  // Success: the full card replaces the panel so the payer sees
  // "paid!" without scrolling past a grayed-out "Pay" button.
  if (phase === "success" && receipt) {
    return <ReceiptCard receipt={{ receipt }} link={link} method="direct" />;
  }

  if (!isConnected) {
    return (
      <div className="pay-panel">
        <h3 className="pay-panel__heading">Connect your wallet</h3>
        <p className="pay-panel__subheading">
          Direct transfer requires a connected wallet that holds{" "}
          {link.tokenSymbol} on {link.network}. You'll pay the gas fee.
        </p>
        <div className="pay-connectors">
          {connectors.map((connector) => (
            <button
              key={connector.id}
              className="pay-connector"
              onClick={() => connect({ connector })}
              disabled={!connector.ready || isConnecting}
            >
              {connector.name}
              {isConnecting &&
                pendingConnector?.id === connector.id &&
                " (connecting…)"}
              {!connector.ready && " (unavailable)"}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Connected: render the direct-transfer description, status
  // strip, and the Pay button whose label tracks `phase`.
  //
  // CRITICAL-fix two-popup UX: post-tx we ask the payer to sign
  // a plain-text attestation so the backend can authenticate the
  // receipt claim without mutating on-chain amounts. Copy makes
  // the two steps explicit ("Step 1 of 2" / "Step 2 of 2") so
  // users aren't confused by the second wallet popup after they
  // already "paid".
  const explorerUrl = txHash ? getTxExplorerUrl(link.chainId, txHash) : null;
  const isWorking =
    phase === "switching" ||
    phase === "sending" ||
    phase === "attesting" ||
    phase === "confirming" ||
    phase === "recording";

  let buttonLabel;
  switch (phase) {
    case "switching":
      buttonLabel = "Switching network…";
      break;
    case "sending":
      buttonLabel = "Step 1 of 2 — Confirm in wallet…";
      break;
    case "attesting":
      buttonLabel = "Step 2 of 2 — Sign confirmation in wallet…";
      break;
    case "confirming":
      // Show the confirmation counter when the backend has reported
      // one — makes the 30s Polygon wait feel much shorter.
      if (requiredConfirmations) {
        buttonLabel = `Confirming (${confirmations}/${requiredConfirmations})…`;
      } else {
        buttonLabel = "Waiting for confirmations…";
      }
      break;
    case "recording":
      buttonLabel = "Recording payment…";
      break;
    case "error":
      buttonLabel = `Pay ${link.amountDisplay} (direct)`;
      break;
    default:
      buttonLabel = `Pay ${link.amountDisplay} (direct)`;
  }

  return (
    <div className="pay-panel">
      <h3 className="pay-panel__heading">
        <FaExchangeAlt size={14} style={{ marginRight: 6 }} />
        Pay with direct transfer
      </h3>
      <div className="pay-direct-info">
        <p className="pay-panel__subheading">
          You will send <strong>{link.amountDisplay}</strong> to{" "}
          <code className="pay-direct-info__recipient">
            {link.payTo.slice(0, 6)}…{link.payTo.slice(-4)}
          </code>
          .
        </p>
        <p className="pay-direct-info__note">
          You pay the on-chain gas fee.
          {link.isNative &&
            " Your wallet will use the exact atomic amount — the display is rounded."}
        </p>
      </div>

      <div className="pay-panel__subheading">
        Connected as{" "}
        <code>
          {address?.slice(0, 6)}…{address?.slice(-4)}
        </code>{" "}
        <button
          type="button"
          onClick={() => {
            reset();
            disconnect();
          }}
          style={{
            background: "transparent",
            border: "none",
            color: "#534ab7",
            cursor: "pointer",
            padding: 0,
            textDecoration: "underline",
            fontSize: "inherit",
          }}
        >
          Change
        </button>
      </div>

      {phase === "error" && error && (
        <div className="pay-status pay-status--error">{error.message}</div>
      )}
      {phase === "switching" && (
        <div className="pay-status pay-status--info">
          Switching your wallet to {link.network}…
        </div>
      )}
      {phase === "sending" && (
        <div className="pay-status pay-status--info">
          <strong>Step 1 of 2:</strong> Send the payment. Confirm the transfer
          in your wallet.
        </div>
      )}
      {phase === "attesting" && (
        <div className="pay-status pay-status--info">
          <strong>Step 2 of 2:</strong> Sign a confirmation in your wallet to
          prove this transfer is yours. No gas, no funds move.
        </div>
      )}
      {phase === "confirming" && (
        <div className="pay-status pay-status--info">
          Waiting for on-chain confirmations{" "}
          {requiredConfirmations
            ? `(${confirmations}/${requiredConfirmations})`
            : ""}
          {explorerUrl && (
            <>
              {" "}
              <a
                href={explorerUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="pay-direct-info__tx-link"
              >
                View on explorer <FaExternalLinkAlt size={10} />
              </a>
            </>
          )}
        </div>
      )}

      <button
        type="button"
        className="pay-button"
        onClick={() => {
          if (phase === "error") reset();
          pay();
        }}
        disabled={isWorking}
      >
        <FaLock size={14} />
        {buttonLabel}
      </button>
    </div>
  );
}
